import { useEffect, useRef, useState } from 'react';
import type { AccessState, PortalInfo, PortalMe } from '@bbbk/schema';
import { homeStays } from '@bbbk/schema';
import { upcomingStay } from './homeRules';
import { api, ApiError } from '../lib/api';
import { isoDate, startOfDay } from '../lib/dates';
import { JoinDoor, type RefusalReason } from './JoinDoor';
import { CustomerSignIn } from './CustomerSignIn';
import { CUSTOMER_SIGNIN_PATH, doorReplyFromError, offersSignIn, type DoorFailure } from './joinOutcome';
import { JoinBusy } from './JoinBusy';
import { HomeView } from './HomeView';
import { Drawer } from '../components/common/Drawer';
import { InfoSoon } from './InfoSoon';
import { signOutSession } from '../lib/signOut';
import { CustomerLockedOut, type LockedOutReason } from './CustomerLockedOut';
import { Registration } from './Registration';
import { RequestWizard } from './RequestWizard';
import { readPending } from './requestRules';
import { useLocation, useNavigate } from 'react-router-dom';
import { CUSTOMER_BOOK, CUSTOMER_DOGS, CUSTOMER_HOME, CUSTOMER_INFO, CUSTOMER_PAY } from './landing';
import { PaySheet, markProofSent, paySheetStay, sheetKind } from './PaySheet';
import { DogsSheet } from './DogsSheet';
import { keepIfSame, latestOnly, startLiveRefresh } from './liveRefresh';
import { CustomerToastProvider, useCustomerToast } from './CustomerToast';

/** The drawer's leave (sheet.css `.dw.out`, 340ms). */
const DRAWER_LEAVE_MS = 340;

// The customer app: two screens, their own shell, and no admin chrome ever
// (R-IN-1, §4). Mounted by App.tsx ahead of the desk — a customer must never
// see the desk's sidebar flash before being told it is not theirs.

/** What GET /api/portal/invite/:token answers. */
interface InviteReply {
  state: AccessState;
  contactName: string | null;
  expiresAt: string;
}

function refusalFrom(params: URLSearchParams): RefusalReason | null {
  const refused = params.get('refused');
  return refused === 'account_in_use' || refused === 'link_dead' ? refused : null;
}

/**
 * `/join/:token` — the door.
 *
 * Reaching it is what hands the browser its invite cookie, so this page has to
 * load before the customer taps a provider, and the request is the page rather
 * than an optimisation on top of it. A token that is not ours renders the same
 * dead door as an expired one: a stranger guessing tokens learns nothing from
 * the difference.
 */
export function JoinPage({ token }: { token: string }) {
  const [reply, setReply] = useState<InviteReply | null | DoorFailure>(null);
  // Bumped by the retry on the unreachable door — the effect below is keyed on
  // the token, which has not changed, so without this nothing would re-fetch.
  const [attempt, setAttempt] = useState(0);
  const params = new URLSearchParams(window.location.search);
  const refused = refusalFrom(params);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api<InviteReply>(`/portal/invite/${encodeURIComponent(token)}`, {
          // The sign-in page is reached, left for a provider, and returned to.
          // A cached "this link is good" would survive the very moment it
          // stopped being true.
          cache: 'reload',
          redirectOn401: false,
        });
        if (!cancelled) setReply(res.data);
      } catch (e) {
        if (cancelled) return;
        setReply(doorReplyFromError(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  if (reply === null) return <main className="mp-page" aria-busy="true" />;
  if (reply === 'busy') return <JoinBusy />;
  // NEITHER A BAD LINK NOR A HOT ONE — the API is down or the network went.
  // Telling somebody holding a perfectly good link that it is not ours, with
  // no way to try again, is asserting a cause nothing established (P2.21).
  if (reply === 'unreachable') {
    return (
      <CustomerLockedOut
        reason="unreachable"
        onRetry={() => {
          setReply(null);
          setAttempt((n) => n + 1);
        }}
      />
    );
  }
  const found = reply === 'missing' ? null : reply;

  return (
    <JoinDoor
      state={found?.state ?? 'none'}
      contactName={found?.contactName ?? null}
      refused={refused}
      // An old link explains itself and then offers the way on, rather than
      // moving somebody somewhere without saying why.
      offerSignIn={found !== null && refused === null && offersSignIn(found.state)}
      token={token}
    />
  );
}

/**
 * `/home` — where a bound customer lands, and where the OAuth callback sends
 * them (AC9a).
 *
 * A 401 here means the session went while they were looking at it — revoked at
 * the desk, or simply expired — so the page sends them back to the front door
 * rather than showing a shell with nothing in it.
 */
export function CustomerHome() {
  return <CustomerToastProvider><CustomerHomeInner /></CustomerToastProvider>;
}

function CustomerHomeInner() {
  const location = useLocation();
  const navigate = useNavigate();
  // A SEND WHOSE REPLY NEVER ARRIVED (AC22) — read once, on the first render:
  // the wizard takes it from here and asks the server again with the same id.
  // Cleared when that wizard is done, so the drawer it opened can close.
  const [resume, setResume] = useState(() => readPending());
  const booking = location.pathname === CUSTOMER_BOOK || resume !== null;
  const [me, setMe] = useState<PortalMe | null>(null);
  // Info's settings — read beside /me, quietly: a failed read leaves the
  // handover windows and the bank details blank rather than taking the page.
  // Undefined while the first read is out, so the pay sheet waits rather than
  // claiming there are no bank details (PR #255 review, #09); null once read and empty, or unreadable.
  const [info, setInfo] = useState<PortalInfo | null | undefined>(undefined);
  // Only the newest /me may land (PR #255 review, #63).
  const reads = useRef(latestOnly());
  const [lockedOut, setLockedOut] = useState<LockedOutReason | null>(null);
  const [failed, setFailed] = useState(false);
  // Set when registration completed IN THIS TAB: the receipt stays up until
  // its own button is tapped, even though `/me` now says registered.
  const [onReceipt, setOnReceipt] = useState(false);
  // The drawer's pull and Escape ask the wizard to close, the way its X does.
  // A hook, so it lives here with the others, above every early return.
  const wizardClose = useRef<(() => void) | null>(null);
  const payClose = useRef<(() => void) | null>(null);
  const dogsClose = useRef<(() => void) | null>(null);
  const toast = useCustomerToast();
  // THE PAGE BEHIND THE DRAWER stays the page Book was tapped on (#245 review):
  // `/book` is the wizard's URL, so the route alone says nothing about what is
  // underneath, and Home was drawn behind a drawer opened from Info.
  const kind = customerRoute(location.pathname)?.kind;
  const [behind, setBehind] = useState<'home' | 'info'>('home');
  useEffect(() => {
    if (kind === 'info') setBehind('info');
    else if (kind === 'home') setBehind('home');
  }, [kind]);

  // `quiet` — a re-read from INSIDE a mounted flow, where the caller shows its
  // own error and its own retry.
  //
  // Registration calls this after its write lands. Without the flag, a failed
  // re-read set `failed` and swapped the whole page for the unreachable screen,
  // so the retry the dogs step had just grown was never on screen to press and
  // the once-only receipt was still lost (claude[bot] P1, 2026-09-15). My own
  // two fixes were cancelling each other out.
  //
  // 401 and 403 still take over the page even when quiet: those are facts about
  // the session, not a hiccup, and the flow cannot recover from either.
  const load = async (opts: { quiet?: boolean } = {}) => {
    try {
      const n = reads.current.begin();
      const res = await api<PortalMe>('/portal/me', { redirectOn401: false, cache: 'reload' });
      // A newer read, or a sent screenshot marked on screen, started since: this snapshot is stale.
      if (!reads.current.isLatest(n)) return res.data;
      // An unchanged read keeps the object on screen, so a live refresh that finds nothing redraws nothing.
      setMe((prev) => keepIfSame(prev, res.data));
      void api<PortalInfo>('/portal/info', { redirectOn401: false })
        .then((r) => setInfo((prev) => keepIfSame(prev ?? null, r.data)))
        .catch(() => setInfo((prev) => (prev === undefined ? null : prev)));
      return res.data;
    } catch (e) {
      // 401 AND 403 ARE DIFFERENT FACTS and get different pages. A 401 means
      // the session went and signing in again is the way back; a 403 means
      // this account is signed in and simply is not a customer, where a
      // sign-in button would land them straight back here. Collapsing the two
      // is what told Freddi his access had ended when it had not.
      if (e instanceof ApiError && e.status === 401) setLockedOut('signed-out');
      // The ONLY 403 that can reach here now is a broken grant: a desk role
      // never mounts this app (customer/landing.ts). Branching on the code
      // rather than the status keeps the page from asserting a cause the
      // response has not established — the rule this file learned the hard way.
      else if (e instanceof ApiError && e.status === 403 && e.code === 'no_household') setLockedOut('no-household');
      // NOT A REFUSAL. A 500, a dropped connection or a timeout says nothing
      // about their session, and telling them they are signed out is the exact
      // mistake CustomerLockedOut's own header warns about (issue 19).
      else if (!opts.quiet) setFailed(true);
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    void load().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // LIVE, BUT NEVER UNDER THEIR THUMB (2026-10-11): Home and Info re-read on a cadence and on
  // coming back into view; while a drawer is open (the pay sheet, the booking wizard) or the
  // person is mid-registration, nothing refreshes — and closing the drawer reads once.
  const live = !!me && !!me.contact.registeredAt && kind !== 'pay' && kind !== 'dogs' && !booking && !onReceipt;
  useEffect(() => {
    if (!live) return;
    void load({ quiet: true });
    return startLiveRefresh({ refresh: () => void load({ quiet: true }) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  if (lockedOut) return <CustomerLockedOut reason={lockedOut} />;
  if (failed) return <CustomerLockedOut reason="unreachable" onRetry={() => { setFailed(false); void load(); }} />;
  if (!me) return <main className="mp-page" aria-busy="true" />;

  // NOT REGISTERED, NOT HOME (AC2). A bound person with no `registered_at` is
  // taken to *Your details* whatever customer URL they opened, and reaches
  // Home only through the receipt. Registration re-reads `/me` after each
  // step, so a customer who closed the tab resumes prefilled.
  if (!me.contact.registeredAt || onReceipt) {
    return (
      <Registration
        me={me}
        reload={() => load({ quiet: true })}
        onRegistered={() => setOnReceipt(true)}
        onDone={() => {
          setOnReceipt(false);
          window.history.replaceState(null, '', '/home');
        }}
      />
    );
  }

  // THE WIZARD RIDES IN THE DRAWER, OVER THE PAGE (Freddi, 2026-10-09;
  // deviation 19): Book slides it up from the foot to fill the screen, and
  // Home — or Info — stays underneath. The wizard keeps its own URL (`/book`)
  // so the browser's Back closes it; its X asks before it discards and then
  // closes it the same way. The drawer unmounts the wizard once it is down,
  // so every opening starts clean.
  const closeBook = () => {
    // Back to the page it rose from when there is one behind; otherwise Home.
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(CUSTOMER_HOME, { replace: true });
  };
  const wizard = (
    <Drawer open={booking} label="Book a stay" onClose={() => wizardClose.current?.()}>
      <RequestWizard
        closeRef={wizardClose}
        me={{ ...me, today: me.today || isoDate(startOfDay(new Date())) }}
        resume={resume}
        onLockedOut={(reason) => setLockedOut(reason)}
        onDone={(sent) => {
          // A send that may have landed is kept, and Home asks again with
          // the same id — by reloading, so the pending read at mount is the
          // one that resumes it (AC22), as before the drawer.
          if (readPending()) { window.location.assign(CUSTOMER_HOME); return; }
          setResume(null);
          // A request sent lands on Home, where it is listed (AC12) — not back
          // on the page Book was tapped from (#245 review: Info). Home re-reads.
          if (sent) { navigate(CUSTOMER_HOME, { replace: true }); void load({ quiet: true }); return; }
          closeBook();
        }}
      />
    </Drawer>
  );

  // THE PAY SHEET RIDES THE SAME DRAWER (deposit-proof AC1; Freddi, 2026-10-10):
  // `/pay/<id>` over Home, Back closes it, X asks when a file is attached.
  // Sent: the drawer closes and Home re-reads — *Patricia is checking your
  // deposit* is the confirmation (AC6). A stay the URL names that is not
  // theirs, or has nothing to pay, simply shows Home.
  const payRoute = customerRoute(location.pathname);
  const payStay = payRoute?.kind === 'pay' ? paySheetStay(me.stays, payRoute.id) : null;
  const closePay = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(CUSTOMER_HOME, { replace: true });
  };
  const paySheet = (
    <Drawer open={payStay !== null} label="Pay" onClose={() => payClose.current?.()}>
      {payStay && (
        <PaySheet
          closeRef={payClose}
          stay={payStay}
          dogs={me.dogs}
          info={info}
          contactName={me.contactName}
          onLockedOut={setLockedOut}
          onDone={(sent) => {
            if (sent) {
              // Sent at once (PR #255 review, #38): the stay reads waiting from the server's yes,
              // and any read already in flight is outrun, so it cannot draw the old door back.
              const sentKind = sheetKind(payStay);
              if (sentKind) { reads.current.begin(); setMe((prev) => (prev ? markProofSent(prev, payStay.id, sentKind) : prev)); }
              navigate(CUSTOMER_HOME, { replace: true }); void load({ quiet: true });
              // After the drawer has slid away (its leave is 340ms), so the toast lands on a still Home and is seen.
              toast({ title: 'Screenshot sent', detail: 'Patricia will check it — usually the same day', afterMs: DRAWER_LEAVE_MS + 120 });
              return;
            }
            closePay();
          }}
        />
      )}
    </Drawer>
  );

  // The kennel's day, from the server — see PortalMe.today (P2.32).
  const today = me.today || isoDate(startOfDay(new Date()));

  // THE DOGS DRAWER RIDES THE SAME DRAWER (vaccination-cards AC1; Freddi, 2026-10-13: "follow the
  // same procedure as deposit proof"): `/your-dogs` over Home, Back closes it, X asks when a card is
  // attached. Sent with no other dog waiting: the drawer closes, Home re-reads and the toast says so;
  // with one waiting, the drawer stays on that dog (AC1b) and Home re-reads behind it.
  const dogsOpen = customerRoute(location.pathname)?.kind === 'dogs';
  const closeDogs = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(CUSTOMER_HOME, { replace: true });
  };
  const dogsSheet = (
    <Drawer open={dogsOpen} label="Your dogs" compact onClose={() => dogsClose.current?.()}>
      {dogsOpen && (
        <DogsSheet
          closeRef={dogsClose}
          dogs={me.dogs}
          stay={upcomingStay(homeStays(me.stays, today), today)}
          today={today}
          contactName={me.contactName}
          wanted={new URLSearchParams(location.search).get('dog')}
          onLockedOut={setLockedOut}
          onStale={() => void load({ quiet: true })}
          onLeave={closeDogs}
          onSent={(_dogId, next) => {
            void load({ quiet: true });
            if (next) return;
            navigate(CUSTOMER_HOME, { replace: true });
            toast({ title: 'Card sent', detail: 'Patricia will check it — usually the same day', afterMs: DRAWER_LEAVE_MS + 120 });
          }}
        />
      )}
    </Drawer>
  );
  const shell = { contactName: me.contactName, onBook: () => navigate(CUSTOMER_BOOK), onGo: (path: string) => navigate(path) };
  const route = customerRoute(location.pathname);
  if (route?.kind === 'info' || (booking && behind === 'info')) {
    return (
      <>
      {/* INFO IS PARKED (Freddi, 2026-10-09): coming soon, with the way out.
          The built page is `InfoPage.tsx`, carried by the customer-info story. */}
      <InfoSoon
        {...shell}
        email={me.contact.email}
        // A CUSTOMER GOES TO THE CUSTOMER'S DOOR. `/` is the desk's root.
        onSignOut={() => signOutSession(() => {}, CUSTOMER_SIGNIN_PATH)}
      />
      {wizard}
      </>
    );
  }
  return (
    <>
      <HomeView {...shell} dogs={me.dogs} stays={me.stays} declined={me.declined ?? []} today={today} info={info ?? null} />
      {wizard}
      {paySheet}
      {dogsSheet}
    </>
  );
}

/**
 * Which customer screen this URL is, or null for "not a customer URL at all".
 *
 * A PLAIN MATCH RATHER THAN A ROUTER, deliberately. These two paths are read
 * before the session probe resolves — the door in particular must render for
 * somebody who has no session and is not supposed to have one yet — and
 * mounting react-router to answer a question this shape would put the desk's
 * whole routing tree in front of a page that has no nav.
 */
export function customerRoute(
  pathname: string,
): { kind: 'join'; token: string } | { kind: 'home' } | { kind: 'register' } | { kind: 'book' } | { kind: 'signin' } | { kind: 'info' } | { kind: 'pay'; id: string } | { kind: 'dogs' } | null {
  if (pathname === '/home') return { kind: 'home' };
  // The pay sheet (deposit-proof AC1): its own URL over Home; a bare /pay is Home.
  const pay = new RegExp('^' + CUSTOMER_PAY + '/([^/]+)/?$').exec(pathname);
  if (pay) {
    // A malformed escape is Home, never a crash (PR #255 review, #28).
    try { return { kind: 'pay', id: decodeURIComponent(pay[1]!) }; } catch { return { kind: 'home' }; }
  }
  if (pathname === CUSTOMER_PAY) return { kind: 'home' };
  // The dogs drawer (vaccination-cards AC1), over Home. The desk keeps `/dogs`.
  if (pathname === CUSTOMER_DOGS) return { kind: 'dogs' };
  // An old stay link is the customer's, signed in or out (#245 review).
  if (pathname.startsWith('/stays/')) return { kind: 'home' };
  // Info (customer-home-stays AC9), which carries the dogs (AC7, deviation 17).
  // There is no stay page and no dogs page: Stays is one level, and an old
  // `/stays/:id` or `/dogs` link lands on Home (deviations 15, 17).
  if (pathname === CUSTOMER_INFO) return { kind: 'info' };
  // The registration steps (customer-sign-in-registration). The same page as
  // Home decides which to draw, from `/me`: an unregistered person gets the
  // steps on either URL, a registered one gets Home on either.
  if (pathname === '/register') return { kind: 'register' };
  if (pathname === CUSTOMER_BOOK) return { kind: 'book' };
  // The customer's durable way in, with no link needed (R-IN-4): a registered
  // person on a new phone, and where a spent link sends whoever follows it.
  if (pathname === CUSTOMER_SIGNIN_PATH) return { kind: 'signin' };
  const join = /^\/join\/([A-Za-z0-9]{1,64})\/?$/.exec(pathname);
  return join ? { kind: 'join', token: join[1]! } : null;
}
